import React, { useEffect, useState } from "react";
import { Row } from "reactstrap";
import ChatbotLauncher from "./chatbot/ChatbotLauncher";
import Select, { SingleValue, ActionMeta, StylesConfig } from "react-select"; // Import from react-select
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { studentCourses, completedLessonByLevel, courseLevel, studentProfile} from "../../../../Api/apiUtils"; // Adjust imports as needed
import Sidebar from "./sideBar";
import MainContent from "./MainContent";
import { CornerDownLeft } from "react-feather";
import { sideButtons } from "./fileSideBar";
import { tasksApi, RM_TASKS_ENABLED } from "../../../../Tasks/tasksApi";
import CreateTaskModal from "../../Tasks/CreateTaskModal";
import { Link } from "react-router-dom";
import { Page, Button, Card, Field } from "../../../../ui";
import { Callout, Stat, StatGroup } from "./ui-pending";
import type { StatTone } from "./ui-pending";
import "./style.scss";
import {
  isTeacherLeaderDisabled,
  TEACHER_LEADER_STUDENT_TABS,
} from "../../../../utils/permissions";


interface CourseData {
  courseName: string;
  courseId: any;
  studentUserId:any

  studentName:any;
  teacherName: string;
  unscheduledClasses: any;
  remainingClasses: any;
  totalClasses: any;
  completedClasses: any;
  teacherUserId: any;
  isDemoCourse:any;
  batchOfflineCourse?: boolean;
  expiredClasses:any;
  scheduledClasses:any;
  teacherId:any
  totalLessons:any
  teacherCredentialId:any
  deductedClasses?: any;
  studentId:any;
  expiryDate:any
}

const getCourseSelectLabel = (course: CourseData) => {
  const name = String(course.courseName || "");
  const tags = [
    course.batchOfflineCourse && !name.toLowerCase().includes("offline")
      ? "Offline"
      : null,
    course.isDemoCourse ? "Trial" : "Paid",
  ].filter(Boolean);
  return `${name} - ${tags.join(" - ")}`;
};

/** react-select themed with design-system tokens (react-select is kept for its search). */
const dsSelectStyles: StylesConfig<{ value: string; label: string }, false> = {
  control: (base, state) => ({
    ...base,
    minHeight: "var(--ds-control-md)",
    borderRadius: "var(--ds-radius-sm)",
    borderColor: state.isFocused ? "var(--ds-primary)" : "var(--ds-border-strong)",
    boxShadow: state.isFocused ? "var(--ds-focus-ring)" : "none",
    backgroundColor: "var(--ds-surface)",
    fontSize: "14px",
  }),
  singleValue: (base) => ({ ...base, color: "var(--ds-text)" }),
  input: (base) => ({ ...base, color: "var(--ds-text)" }),
  placeholder: (base) => ({ ...base, color: "var(--ds-text-subtle)" }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menu: (base) => ({
    ...base,
    zIndex: 9999,
    borderRadius: "var(--ds-radius-sm)",
    boxShadow: "var(--ds-shadow-lift)",
    backgroundColor: "var(--ds-surface)",
    color: "var(--ds-text)",
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? "var(--ds-primary)" : state.isFocused ? "var(--ds-primary-soft)" : "var(--ds-surface)",
    color: state.isSelected ? "var(--ds-text-on-dark)" : "var(--ds-text)",
  }),
};

const ProfilePage: React.FC = () => {
  const [searchParams] = useSearchParams();

  const [activeTab, setActiveTab] = useState(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam) {
      const match = sideButtons.find(
        (b) => b.title.toLowerCase() === tabParam.toLowerCase()
      );
      if (match) return match.title;
    }
    return isTeacherLeaderDisabled()
      ? TEACHER_LEADER_STUDENT_TABS[0]
      : sideButtons[0].title;
  });
  const [selectedCourse, setSelectedCourse] = useState<{ value: string; label: string } | null>(null);
  const [courseData, setCourseData] = useState<CourseData[] | null>(null);
  const [selectedTeacher, setSelectedTeacher] = useState<string>("");
  const [selectedTeacherId, setSelectedTeacherId] = useState<string>("");
  const [selectedExpiry, setSelectedExpiry] = useState<any>("");
  const [selectedTeacherCid, setSelectedTeacherCid] = useState<string>("");
  const [courseslevel, setCoursesLevel] = useState("");
  const [profileData, setProfileData] = React.useState<any>(null);
  const [selectedCourseData, setSelectedCourseData]= useState<any>("");
  const [numberLesson, setNumberLesson]= useState ({
    completedLessonsByLevel:0,
    totalLessons:0
  });
  const [studentUserId, setStudentUserId]= useState("");
  const [rmName, setRmName] = useState<string | null>(null);
  /**
   * "unassigned" is the case worth separating out: a 404 means the RM tool
   * knows nothing about this learner — usually the onboarding event never
   * fired — and that is exactly when someone should be able to put it right
   * from here. Any other failure stays "unavailable" and shows nothing, so a
   * service blip never invites a click that can't work.
   */
  const [rmStatus, setRmStatus] =
    useState<"loading" | "assigned" | "unassigned" | "unavailable">("loading");
  const [assigningRm, setAssigningRm] = useState(false);
  const [raising, setRaising] = useState(false);
  const [rmError, setRmError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Relationship Manager who owns this student (from artium-tasks). Best-effort:
  // hidden when the feature is off or the service is unreachable — never
  // blocks the profile.
  const rmStudentId =
    (courseData && courseData.length > 0 && courseData[0].studentUserId) || studentUserId;
  useEffect(() => {
    if (!RM_TASKS_ENABLED || !rmStudentId) {
      setRmName(null);
      setRmStatus("unavailable");
      return;
    }
    let cancelled = false;
    setRmStatus("loading");
    setRmError(null);
    tasksApi<{ rm: { name: string } }>(`/v1/students/${rmStudentId}/rm`)
      .then((res) => {
        if (cancelled) return;
        const name = res?.rm?.name ?? null;
        setRmName(name);
        setRmStatus(name ? "assigned" : "unassigned");
      })
      .catch((err) => {
        if (cancelled) return;
        setRmName(null);
        setRmStatus(
          (err as { status?: number })?.status === 404 ? "unassigned" : "unavailable",
        );
      });
    return () => { cancelled = true; };
  }, [rmStudentId]);

  /**
   * Put this learner on an RM's list. The RM tool picks who: the timezone
   * gives their country, the country gives the desk, and the least-loaded RM
   * on that desk takes them. Every region has a desk, so it always succeeds.
   *
   * It needs the learner's details because it may never have seen them, and
   * this page already has the profile. The timezone is what routes them, so
   * it is only sent when the profile really holds an IANA zone name; without
   * one they go to the default desk and can be moved from there.
   */
  const handleAssignRm = async () => {
    if (!rmStudentId || !studentDisplayName) return;
    setAssigningRm(true);
    setRmError(null);
    const countryCode = String(profileData?.countryCode || "").replace(/\D/g, "");
    const localNumber = String(profileData?.mobileNumber || "").replace(/\D/g, "");
    const profileTz = String(profileData?.timezone || "");
    try {
      const res = await tasksApi<{ agent: { name: string } }>(
        `/v1/students/${rmStudentId}/assign-rm`,
        {
          method: "POST",
          body: JSON.stringify({
            name: studentDisplayName,
            ...(countryCode && localNumber
              ? { phone: `+${countryCode}${localNumber}` }
              : {}),
            ...(profileData?.emailId ? { email: String(profileData.emailId) } : {}),
            ...(/^[A-Za-z]+\/[A-Za-z0-9_+\-/]+$/.test(profileTz)
              ? { timezone: profileTz }
              : {}),
            metadata: {
              ...(credentialId ? { credentialId: String(credentialId) } : {}),
              ...(displayStudentId ? { studentId: String(displayStudentId) } : {}),
            },
          }),
        },
      );
      const name = res?.agent?.name ?? null;
      setRmName(name);
      setRmStatus(name ? "assigned" : "unassigned");
    } catch (err) {
      // The service writes its messages for this reader, so show them
      // as-is. There is no "no RM covers them" case left to explain.
      setRmError((err as Error)?.message || "Could not assign an RM");
    } finally {
      setAssigningRm(false);
    }
  };

  const [courseCounts, setCourseCounts] = useState({
    unscheduledClasses: 0,
    completedClasses: 0,
    scheduledClasses: 0,
    expiredClasses:0,
    deductedClasses: 0,
  });
  
  const { credentialId, studentId, userId, userType } = useParams<{
    
    credentialId: any;
    studentId: any;
    userId: any;
    userType: any;
  }>();
  const navigate = useNavigate();

  const handleCourseChange = async (
    newValue: SingleValue<{ value: string; label: string }>,
    actionMeta: ActionMeta<{ value: string; label: string }>
  ) => {
    if (newValue) {
      setSelectedCourse(newValue);
      localStorage.setItem("selectedCourse", newValue.value);
      setNumberLesson({ completedLessonsByLevel: 0, totalLessons: 0 });
  
      const selectedCourseObj = courseData?.find((course) => course.courseId === newValue.value);
      if (selectedCourseObj) {
        setSelectedTeacher(selectedCourseObj.teacherName);
        localStorage.setItem("teacherUserId", selectedCourseObj.teacherUserId);
        setSelectedTeacherId(selectedCourseObj.teacherId);
        setSelectedExpiry(selectedCourseObj.expiryDate);
        setSelectedTeacherCid(selectedCourseObj.teacherCredentialId)
        localStorage.setItem("SelectedCourseTeacherId", selectedCourseObj.teacherId);
        setStudentUserId(selectedCourseObj.studentUserId);
        setSelectedCourseData(selectedCourseObj)
  
        setCourseCounts({
          unscheduledClasses: selectedCourseObj.unscheduledClasses || 0,
          completedClasses: selectedCourseObj.completedClasses || 0,
          expiredClasses: selectedCourseObj.expiredClasses || 0,
          scheduledClasses: selectedCourseObj.scheduledClasses || 0,
          deductedClasses: selectedCourseObj.deductedClasses || 0,
        });
  
        // Fetch the new level and lessons in sequence
        try {
          if (!selectedCourseObj.studentUserId) {
            setNumberLesson({ completedLessonsByLevel: 0, totalLessons: 0 });
            return;
          }
          const levelResponse = await courseLevel(selectedCourseObj.studentUserId, newValue.value);
          const levelId = levelResponse?.status?.[0]?.level_id;
          
          if (levelId) {
            // Update level and wait for it to complete
            await new Promise(resolve => {
              setCoursesLevel(levelId);
              resolve(null);
            });
            
            // Now fetch lessons with the new levelId
            const lessonsResponse = await completedLessonByLevel(selectedCourseObj.studentUserId, levelId);
            setNumberLesson(lessonsResponse || { completedLessonsByLevel: 0, totalLessons: 0 });
          } else {
            console.error("level_id not found in response");
            setNumberLesson({ completedLessonsByLevel: 0, totalLessons: 0 });
          }
        } catch (error) {
          console.error("Failed to fetch course level or lessons:", error);
          setNumberLesson({ completedLessonsByLevel: 0, totalLessons: 0 });
        }
      }
    } else {
      // Handle null case
      setSelectedCourse(null);
      localStorage.removeItem("selectedCourse");
      setSelectedTeacher("");
      localStorage.removeItem("teacherUserId");
      setCourseCounts({
        unscheduledClasses: 0,
        completedClasses: 0,
        scheduledClasses: 0,
        expiredClasses: 0,
        deductedClasses: 0,
      });
      setNumberLesson({ completedLessonsByLevel: 0, totalLessons: 0 });
    }
  };
  // useEffect(() => {

  //   const fetchProfileData = async () => {
  // const Response = await studentProfile(credentialId, studentId);
  // setProfileData(Response)
  // console.log("data1", Response)
  //   };

  //   fetchProfileData();
  // }, []);

  useEffect(() => {
    const fetchProfileData = async () => {
      if (!credentialId || !studentId) return;
      try {
        const response = await studentProfile(credentialId, studentId);
        setProfileData(response);
      } catch (error) {
        console.error("Failed to fetch student profile:", error);
      }
    };

    fetchProfileData();
  }, [credentialId, studentId]);

  
  const fetchCourses = async () => {
    try {
      const response = await studentCourses(studentId);
  
      if (Array.isArray(response) && response.length > 0) {
        setCourseData(response);
  
        const storedCourseId = localStorage.getItem("selectedCourse");
        const defaultCourse = response.find((course) => course.courseId === storedCourseId) || response[0];
  
        // Set all course-related state first
        setSelectedCourse({ value: defaultCourse.courseId, label: getCourseSelectLabel(defaultCourse) });
        setSelectedTeacher(defaultCourse.teacherName);
        localStorage.setItem("selectedCourse", defaultCourse.courseId);
        setStudentUserId(defaultCourse.studentUserId);
        setSelectedTeacherCid(defaultCourse.teacherCredentialId)
        localStorage.setItem("teacherUserId", defaultCourse.teacherUserId);
        setSelectedTeacherId(defaultCourse.teacherId);
        setSelectedExpiry(defaultCourse.expiryDate);
        setSelectedCourseData(defaultCourse)
        localStorage.setItem("SelectedCourseTeacherId", defaultCourse.teacherId);
  
        setCourseCounts({
          unscheduledClasses: defaultCourse.unscheduledClasses || 0,
          expiredClasses: defaultCourse.expiredClasses || 0,
          completedClasses: defaultCourse.completedClasses || 0,
          scheduledClasses: defaultCourse.scheduledClasses || 0,
          deductedClasses: defaultCourse.deductedClasses || 0,
        });
  
        // Now fetch the level and lessons for the default course
        try {
          if (!defaultCourse.studentUserId) {
            setNumberLesson({ completedLessonsByLevel: 0, totalLessons: 0 });
            return;
          }
          const levelResponse = await courseLevel(defaultCourse.studentUserId, defaultCourse.courseId);
          const levelId = levelResponse?.status?.[0]?.level_id;
          
          if (levelId) {
            setCoursesLevel(levelId);
            const lessonsResponse = await completedLessonByLevel(defaultCourse.studentUserId, levelId);
            setNumberLesson(lessonsResponse || { completedLessonsByLevel: 0, totalLessons: 0 });
          } else {
            console.error("level_id not found in response");
            setNumberLesson({ completedLessonsByLevel: 0, totalLessons: 0 });
          }
        } catch (error) {
          console.error("Failed to fetch course level or lessons:", error);
          setNumberLesson({ completedLessonsByLevel: 0, totalLessons: 0 });
        }
      }
    } catch (err) {
      console.error("Failed to fetch courses:", err);
    }
  };
  


  const fetchCourseLevel = async () => {
    try {
      const courseId = selectedCourse?.value;
      const userId = studentUserId;
      console.log (courseId, "courselveelid")
      if (!courseId || !userId) {
        console.error("Missing courseId or userId");

        setNumberLesson({ completedLessonsByLevel: 0, totalLessons: 0 });
        return;
      }
  
      const response = await courseLevel(userId, courseId);
      const levelId = response?.status?.[0]?.level_id;
      console.log ("levelID", levelId)
      if (levelId) {
        // First update the level state and wait for it to complete
        await new Promise(resolve => {
          setCoursesLevel(levelId);
          resolve(null);
        });
        
        console.log("Current courseslevel:", courseslevel); 
        await fetchCompletedLessons();
      } else {
        console.error("level_id not found in response");
        setNumberLesson({ completedLessonsByLevel: 0, totalLessons: 0 });
        // setCoursesLevel("")
      }
    } catch (error) {
      console.error("Failed to fetch course level:", error);
   
      setNumberLesson({ completedLessonsByLevel: 0, totalLessons: 0 });
    }
  };


  const fetchCompletedLessons = async () => {
    const userId = studentUserId;
    const levelId = courseslevel;
  
    // Reset to 0/0 if missing required values
    if (!userId || !levelId) {
      setNumberLesson({ completedLessonsByLevel: 0, totalLessons: 0 });
      return;
    }
  
    try {
      const response = await completedLessonByLevel(userId, levelId);
      setNumberLesson(response || { completedLessonsByLevel: 0, totalLessons: 0 });
      console.log("lessonnumber", response);
    } catch (err) {
      console.error("Failed to fetch completed lessons:", err);
      // Set to 0/0 on error
      setNumberLesson({ completedLessonsByLevel: 0, totalLessons: 0 });
    }
  };

  
  useEffect(() => {
    fetchCourses();
  }, [studentId]);

  const courseStudentName = String(courseData?.[0]?.studentName || "").trim();
  const profileStudentName = [
    profileData?.firstName,
    profileData?.lastName,
  ]
    .filter(Boolean)
    .join(" ")
    .trim() || String(profileData?.studentName || profileData?.name || "").trim();
  const studentDisplayName = courseStudentName || profileStudentName;
  const displayStudentId =
    courseData?.[0]?.studentId ||
    profileData?.studentId ||
    studentId;
  const displayUserId =
    courseData?.[0]?.studentUserId ||
    profileData?.userId ||
    profileData?.uId ||
    userId;

  const statTone = (label: string): StatTone =>
    label === "Deducted" ? "danger" : label === "Scheduled" || label === "Unscheduled" ? "info" : label === "Expired" ? "default" : "success";

  return (
    <Page className="student-profile-page">
      <Card className="sp-header">
        <div className="sp-toolbar">
          <Button
            variant="ghost"
            size="sm"
            icon={<CornerDownLeft size={16} aria-hidden />}
            onClick={() => navigate(-1)}
          >
            Back
          </Button>
          <div className="sp-identity">
            <span className="sp-identity__eyebrow">Student</span>
            <h1 className="sp-identity__name">{studentDisplayName || "N/A"}</h1>
          </div>
          <div className="sp-chips">
            {selectedTeacher ? (
              <span className="sp-chip">
                Teacher{" "}
                <Link to={`/pages/teacher-profile/${selectedTeacherCid}/${selectedTeacherId}`}>{selectedTeacher}</Link>
              </span>
            ) : null}
            {displayUserId ? (
              <span className="sp-chip">
                UserId <b>{displayUserId}</b>
              </span>
            ) : null}
            {displayStudentId ? (
              <span className="sp-chip">
                StudentId <b>{displayStudentId}</b>
              </span>
            ) : null}
            {rmStatus === "assigned" && rmName ? (
              <span className="sp-chip">
                RM <b>{rmName}</b>
                {!isTeacherLeaderDisabled() && (
                  <Button
                    variant="ghost"
                    size="sm"
                    title="Move this learner to a different RM, and see the history"
                    onClick={() => setActiveTab("Relationship Manager")}
                  >
                    Change
                  </Button>
                )}
              </span>
            ) : null}
          </div>
          <div className="sp-toolbar__actions">
            {RM_TASKS_ENABLED && rmStatus === "assigned" && studentDisplayName && !isTeacherLeaderDisabled() && (
              <Button
                variant="secondary"
                onClick={() => setRaising(true)}
                title={`Raise a ticket for ${rmName ?? "their RM"} to pick up`}
              >
                Raise ticket
              </Button>
            )}
            {rmStatus === "unassigned" && studentDisplayName && !isTeacherLeaderDisabled() && (
              <Button
                variant="accent"
                onClick={handleAssignRm}
                loading={assigningRm}
                title="No RM owns this student yet — assign the least-loaded RM on the desk that covers their country"
              >
                {assigningRm ? "Assigning…" : "Assign RM"}
              </Button>
            )}
          </div>
        </div>

      {raising && rmStudentId && studentDisplayName && (
        <CreateTaskModal
          studentExternalId={String(rmStudentId)}
          studentName={studentDisplayName}
          rmName={rmName}
          phoneE164={(() => {
            const cc = String(profileData?.countryCode || "").replace(/\D/g, "");
            const num = String(profileData?.mobileNumber || "").replace(/\D/g, "");
            return cc && num ? `+${cc}${num}` : undefined;
          })()}
          email={profileData?.emailId ? String(profileData.emailId) : undefined}
          onClose={() => setRaising(false)}
        />
      )}

      {rmError && <Callout tone="danger">{rmError}</Callout>}

      <div className="sp-summary">
        <StatGroup title="Classes" className="sp-summary__classes">
          {[
            ...(selectedCourseData?.isDemoCourse ? [] : [{ label: "Purchased", value: selectedCourseData?.totalClasses }]),
            { label: "Completed", value: courseCounts.completedClasses },
            { label: "Scheduled", value: courseCounts.scheduledClasses },
            { label: "Unscheduled", value: courseCounts.unscheduledClasses },
            ...(selectedCourseData?.isDemoCourse
              ? []
              : [
                  { label: "Expired", value: courseCounts.expiredClasses },
                  { label: "Deducted", value: courseCounts.deductedClasses },
                ]),
          ].map((stat) => (
            <Stat key={stat.label} label={stat.label} value={stat.value} tone={statTone(stat.label)} />
          ))}
        </StatGroup>

        {/* Lessons Progress - Beside Class Details */}
        {!selectedCourseData.isDemoCourse && (
          <StatGroup title="Lessons" className="sp-summary__lessons">
            <Stat
              label="Completed / Total"
              value={`${numberLesson.completedLessonsByLevel} / ${numberLesson.totalLessons}`}
              tone="primary"
            />
          </StatGroup>
        )}

        <div className="sp-summary__course">
          <Field label="Selected course">
            {({ id }) =>
              courseData && courseData.length > 0 ? (
                <Select
                  inputId={id}
                  value={selectedCourse}
                  onChange={handleCourseChange}
                  options={courseData.map((course) => ({
                    value: course.courseId,
                    label: getCourseSelectLabel(course),
                  }))}
                  placeholder="-- Select --"
                  classNamePrefix="student-course-select"
                  menuPortalTarget={document.body}
                  styles={dsSelectStyles}
                />
              ) : (
                <span className="ds-text-subtle">—</span>
              )
            }
          </Field>
        </div>
      </div>
      </Card>

      <Row className="gx-3 gy-0 student-profile-body">
        <Sidebar activeTab={activeTab} setActiveTab={setActiveTab} />
        <MainContent
          fetchCourses={fetchCourses}
          activeTab={activeTab}
          // The CMS userId the RM tool keys learners by — the same one the
          // RM-name lookup above uses. The route carries no userId.
          cmsUserId={rmStudentId}
          credentialId={credentialId}
          studentId={studentId}
          userId={userId}
          userType={userType}
          courseData={courseData}
          selectedCourse={selectedCourse?.value || ""}
          courseCounts={courseCounts}
          selectedTeacherId={selectedTeacherId}
          selectedExpiry={selectedExpiry}
          selectedTeacherName={selectedTeacher}
          selectedCourseData={selectedCourseData}
        />
      </Row>
      <ChatbotLauncher credentialId={credentialId} studentId={studentId} />
    </Page>
  );
};

export default ProfilePage;
